/** `exportMetadata` sits near the start of `ambrosia.js`; the head is enough to read metadata. */
const AMBROSIA_HEAD_BYTES = 8192;

export interface AmbrosiaLaunchMetadata {
  cohortId: string;
  deploymentId?: string;
  productId?: string;
  tocLevel?: string;
}

function parseMetadataString(metadata: string): AmbrosiaLaunchMetadata | null {
  try {
    const parsed = JSON.parse(metadata) as Partial<AmbrosiaLaunchMetadata>;
    if (!parsed.cohortId) {
      return null;
    }
    return {
      cohortId: parsed.cohortId,
      deploymentId: parsed.deploymentId,
      productId: parsed.productId,
      tocLevel: parsed.tocLevel,
    };
  } catch {
    return null;
  }
}

function extractMetadataFromAmbrosiaHead(ambrosiaHead: string): AmbrosiaLaunchMetadata | null {
  const metadataIndex = ambrosiaHead.indexOf('"metadata"');
  if (metadataIndex === -1) {
    return null;
  }
  const slice = ambrosiaHead.slice(metadataIndex);
  const match = /"metadata"\s*:\s*"((?:\\.|[^"\\])*)"/.exec(slice);
  if (!match?.[1]) {
    return null;
  }
  const unescaped = match[1].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  return parseMetadataString(unescaped);
}

export async function getAmbrosiaLaunchMetadata(
  packagePath: string,
): Promise<AmbrosiaLaunchMetadata | null> {
  if (!packagePath) {
    return null;
  }

  const fromWindow = (window.ambrosia as { exportMetadata?: { metadata?: string } } | undefined)
    ?.exportMetadata?.metadata;
  if (fromWindow) {
    return parseMetadataString(fromWindow);
  }

  const url = `${packagePath.replace(/\/$/, "")}/ambrosia.js`;
  try {
    let res: Response;
    try {
      res = await fetch(url, { headers: { Range: `bytes=0-${AMBROSIA_HEAD_BYTES - 1}` } });
    } catch {
      res = await fetch(url);
    }
    if (!res.ok) {
      return null;
    }
    return extractMetadataFromAmbrosiaHead(await res.text());
  } catch {
    return null;
  }
}
